import { ArrowForward } from '@mui/icons-material';
import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import CButton from '../../components/CButton';
import CLocalizedText from '../../components/CLocalizedText';
import CLocalizedTextStringStatic from '../../components/CLocalizedTextStringStatic';
import CPageModule from '../../components/CPageModule';
import CRequestSuccessPage from '../../components/CRequestSuccessPage';
import CustomTextField from '../../components/CustomTextField';
import PageModuleFormGroup from '../../components/PageModuleFormGroup';
import PageModuleFormRow from '../../components/PageModuleFormRow';
import Page from '../../components/layout/Page';
import { updateStateAttribute } from '../../helpers/helper';
import { useAppDispatch } from '../../hooks/hooks';
import type { IRequestState } from '../../models/IRequestState';
import { LocaleEnum, type TLocales } from '../../models/TLocales';
import { SvConfigServices } from '../../services/SvConfigServices';
import { SvContactFormServices } from '../../services/SvContactFormServices';
import { updateLocale } from '../../store/appSlice';

interface FormModel {
  firstName?: string;
  lastName?: string;
  email: string;
  company?: string;
  position?: string;
  message: string;
}

interface State {
  reqState: IRequestState;
  form: Partial<FormModel>;
  submitted: boolean;
  redirectURL: string;
}

export default function PContactIndex() {
  const [searchParams, setSearchParams] = useSearchParams();
  const injectedToRef = useRef(false);
  const dispatch = useAppDispatch();

  const [state, setState] = useState<State>({
    form: {},
    reqState: { isError: false, isLoading: false },
    submitted: false,
    redirectURL: '/'
  });

  const title = CLocalizedTextStringStatic('contact.title');

  const handleFormChange = (e: React.ChangeEvent, partialForm: Partial<FormModel>) => {
    updateStateAttribute<State>({ form: { ...state.form, ...partialForm } }, setState);
  };

  useEffect(() => {
    (async () => {
      const next = new URLSearchParams(searchParams);
      let shouldReplace = false;

      const rawLocale = searchParams.get('locale')?.toLowerCase();

      const mapLocale = (locale?: string): TLocales | undefined => {
        if (!locale) return undefined;

        for (const localeIterator of Object.values(LocaleEnum)) {
          if (localeIterator.toLowerCase() === locale) return localeIterator;
        }

        return undefined;
      };

      const locale = mapLocale(rawLocale);

      if (locale) {
        dispatch(updateLocale({ locale }));
        updateStateAttribute<State>(
          { redirectURL: (await (await SvConfigServices.getConfig()).getData())?.homepageURL ?? '/' },
          setState
        );
        next.delete('locale');
        shouldReplace = true;
      }

      const to = searchParams.get('to');
      if (to && !injectedToRef.current) {
        const prefix = `[${CLocalizedTextStringStatic('contact.to')}: ${to}]\n\n`;
        const currentMsg = state.form.message ?? '';

        updateStateAttribute<State>(
          {
            form: {
              ...state.form,
              message: `${prefix}${currentMsg}`
            }
          },
          setState
        );

        next.delete('to');
        injectedToRef.current = true;
        shouldReplace = true;
      }

      if (shouldReplace) {
        setSearchParams(next, { replace: true });
      }
    })();
  }, [dispatch, searchParams, setSearchParams]);

  const handleFormSubmit = async () => {
    updateStateAttribute<State>({ reqState: { isLoading: true, isError: false } }, setState);

    const res = await SvContactFormServices.submit(state.form);

    try {
      updateStateAttribute<State>(
        {
          reqState: {
            isLoading: false,
            isError: !(await res.isSuccess()),
            strError: await res.getMessage(),
            objError: await res.getDetail()
          },
          submitted: await res.isSuccess()
        },
        setState
      );
    } catch (error) {
      updateStateAttribute<State>(
        {
          reqState: {
            isLoading: false,
            isError: true
          }
        },
        setState
      );
      throw error;
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;

    const target = e.target as HTMLInputElement | HTMLTextAreaElement;
    const isTextarea = target?.tagName === 'TEXTAREA';

    if (isTextarea) return;

    await handleFormSubmit();
  };

  return (
    <>
      {state.submitted && <CRequestSuccessPage redirectURL={state.redirectURL} />}
      {!state.submitted && (
        <Page title={title}>
          <CPageModule>
            <h1>{title}</h1>
            <PageModuleFormGroup>
              <PageModuleFormRow>
                <CustomTextField
                  label={<CLocalizedText dictKey={'textField.label.firstName'} />}
                  value={state.form.firstName ?? ''}
                  error={(state.reqState.objError?.firstName?.length ?? 0) > 0}
                  helperText={state.reqState.objError?.firstName}
                  onChange={(e) => {
                    handleFormChange(e, { firstName: e.target.value });
                  }}
                  onKeyDown={handleKeyDown}
                />
              </PageModuleFormRow>

              <PageModuleFormRow>
                <CustomTextField
                  label={<CLocalizedText dictKey={'textField.label.lastName'} />}
                  value={state.form.lastName ?? ''}
                  error={(state.reqState.objError?.lastName?.length ?? 0) > 0}
                  helperText={state.reqState.objError?.lastName}
                  onChange={(e) => {
                    handleFormChange(e, { lastName: e.target.value });
                  }}
                  onKeyDown={handleKeyDown}
                />
              </PageModuleFormRow>

              <PageModuleFormRow description={<CLocalizedText dictKey={'textField.label.emailDescription'} />}>
                <CustomTextField
                  label={<CLocalizedText dictKey={'textField.label.email'} />}
                  type="email"
                  value={state.form.email ?? ''}
                  error={(state.reqState.objError?.email?.length ?? 0) > 0}
                  helperText={state.reqState.objError?.email}
                  onChange={(e) => {
                    handleFormChange(e, { email: e.target.value.trim() });
                  }}
                  onKeyDown={handleKeyDown}
                  required
                />
              </PageModuleFormRow>

              <PageModuleFormRow>
                <CustomTextField
                  label={<CLocalizedText dictKey={'textField.label.company'} />}
                  value={state.form.company ?? ''}
                  error={(state.reqState.objError?.company?.length ?? 0) > 0}
                  helperText={state.reqState.objError?.company}
                  onChange={(e) => {
                    handleFormChange(e, { company: e.target.value });
                  }}
                  onKeyDown={handleKeyDown}
                />
              </PageModuleFormRow>

              <PageModuleFormRow>
                <CustomTextField
                  label={<CLocalizedText dictKey={'textField.label.position'} />}
                  value={state.form.position ?? ''}
                  error={(state.reqState.objError?.position?.length ?? 0) > 0}
                  helperText={state.reqState.objError?.position}
                  onChange={(e) => {
                    handleFormChange(e, { position: e.target.value });
                  }}
                  onKeyDown={handleKeyDown}
                />
              </PageModuleFormRow>

              <PageModuleFormRow>
                <CustomTextField
                  label={<CLocalizedText dictKey={'contact.message'} />}
                  value={state.form.message ?? ''}
                  error={(state.reqState.objError?.message?.length ?? 0) > 0}
                  helperText={state.reqState.objError?.message}
                  onChange={(e) => {
                    handleFormChange(e, { message: e.target.value });
                  }}
                  onKeyDown={handleKeyDown}
                  required
                  multiline
                  minRows={6}
                />
              </PageModuleFormRow>
            </PageModuleFormGroup>
            <PageModuleFormGroup>
              <PageModuleFormRow description={<CLocalizedText dictKey={'contact.disclaimer'} />}>
                <CButton
                  isLoading={state.reqState.isLoading}
                  icons={{ end: <ArrowForward /> }}
                  onClick={handleFormSubmit}
                  style={{ borderWidth: '1px', width: '100%', justifyContent: 'center' }}
                  isForm
                >
                  <CLocalizedText dictKey={'contact.submit'} />
                </CButton>
              </PageModuleFormRow>
            </PageModuleFormGroup>
          </CPageModule>
        </Page>
      )}
    </>
  );
}
